import { useState, useEffect, useCallback } from 'react';
import { Zap, Palette } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useSubscription } from '@/hooks/useSubscription';
import { useUserId } from '@/hooks/useUserId';
import { TIER_LIMITS, type TierName, type TierLimits } from '@/constants/tierLimits';
import { resolveTier } from '@/lib/subscription';
import { cn } from '@/lib/utils';

const ROLLING_WINDOW_DAYS = 31;

interface GenerationCounts {
  basicUsed: number;
  enhancedUsed: number;
}

export interface UseGenerationCountsResult {
  loading: boolean;
  basicRemaining: number;
  enhancedRemaining: number;
  basicMax: number;
  enhancedMax: number;
  resolvedTier: TierName | null;
  refresh: () => void;
}

const LOCKOUT_LIMITS: TierLimits = {
  maxInspirationSources: 0,
  maxCompetitors: 0,
  monthlyInspirationAdds: 0,
  monthlyCompetitorAdds: 0,
  monthlyBasicGenerations: 0,
  monthlyEnhancedGenerations: 0,
};

export const useGenerationCounts = (): UseGenerationCountsResult => {
  const userId = useUserId();
  const {
    subscribed,
    productId,
    subscriptionStatus,
    subscriptionTrialEnd,
    subscriptionGracePeriodEnd,
    loading: subscriptionLoading,
  } = useSubscription();
  const [counts, setCounts] = useState<GenerationCounts | null>(null);

  const resolvedTier = resolveTier({
    subscribed,
    status: subscriptionStatus,
    productId,
    subscriptionTrialEnd,
    gracePeriodEnd: subscriptionGracePeriodEnd,
  });
  const limits = resolvedTier ? TIER_LIMITS[resolvedTier] : LOCKOUT_LIMITS;

  const fetchCounts = useCallback(async () => {
    if (!userId) return;

    const { data, error } = await supabase
      .from('generation_counts')
      .select('basic_count, enhanced_count, window_start')
      .eq('user_id', userId)
      .order('window_start', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) {
      setCounts({ basicUsed: 0, enhancedUsed: 0 });
      return;
    }

    const windowStart = new Date(data.window_start);
    const now = new Date();
    const diffDays = (now.getTime() - windowStart.getTime()) / (1000 * 60 * 60 * 24);

    if (diffDays > ROLLING_WINDOW_DAYS) {
      setCounts({ basicUsed: 0, enhancedUsed: 0 });
    } else {
      setCounts({
        basicUsed: data.basic_count ?? 0,
        enhancedUsed: data.enhanced_count ?? 0,
      });
    }
  }, [userId]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchCounts();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchCounts]);

  const basicRemaining = counts ? Math.max(0, limits.monthlyBasicGenerations - counts.basicUsed) : 0;
  const enhancedRemaining = counts ? Math.max(0, limits.monthlyEnhancedGenerations - counts.enhancedUsed) : 0;

  return {
    loading: counts === null || subscriptionLoading,
    basicRemaining,
    enhancedRemaining,
    basicMax: limits.monthlyBasicGenerations,
    enhancedMax: limits.monthlyEnhancedGenerations,
    resolvedTier,
    refresh: fetchCounts,
  };
};

interface GenerationUsageCounterProps {
  data: UseGenerationCountsResult;
}

export const GenerationUsageCounter = ({ data }: GenerationUsageCounterProps) => {
  const { basicRemaining, enhancedRemaining, basicMax, enhancedMax, resolvedTier } = data;

  // Pro plan: only show when 50%+ used on either type
  if (resolvedTier === 'pro') {
    const basicUsedPct = ((basicMax - basicRemaining) / basicMax) * 100;
    const enhancedUsedPct = ((enhancedMax - enhancedRemaining) / enhancedMax) * 100;
    if (basicUsedPct < 50 && enhancedUsedPct < 50) {
      return null;
    }
  }

  return (
    <div className="flex items-center gap-3 px-3 py-1.5 rounded-xl bg-muted/40 border border-border/40 text-sm">
      <CounterPill
        icon={<Zap className="w-3.5 h-3.5" />}
        remaining={basicRemaining}
        max={basicMax}
        label="Podstawowy"
      />
      <div className="w-px h-4 bg-border/50" />
      <CounterPill
        icon={<Palette className="w-3.5 h-3.5" />}
        remaining={enhancedRemaining}
        max={enhancedMax}
        label="Dopasowany"
      />
    </div>
  );
};

interface CounterPillProps {
  icon: React.ReactNode;
  remaining: number;
  max: number;
  label: string;
}

const CounterPill = ({ icon, remaining, max, label }: CounterPillProps) => {
  const pct = ((max - remaining) / max) * 100;
  const isNearLimit = pct >= 80;
  const isAtLimit = remaining <= 0;

  return (
    <div className="flex items-center gap-1.5" title={`${label}: Zostało ${remaining} z ${max}`}>
      <span className={cn(
        "text-muted-foreground",
        isAtLimit && "text-destructive",
        isNearLimit && !isAtLimit && "text-warning",
      )}>
        {icon}
      </span>
      <span className={cn(
        "font-medium tabular-nums",
        isAtLimit ? "text-destructive" : isNearLimit ? "text-warning" : "text-foreground",
      )}>
        {remaining}
      </span>
    </div>
  );
};
